// utils
import { getWidget } from "../shared/utils"
import { study_identification } from "../../dataSchemas/protocol/fieldSchemas"
import { jrctDataSchema } from "../../dataSchemas/protocol/jrctDataSchema"
import { firstProtocolSectionClassNames, protocolClassNames, objectClassNames, arrayClassNames } from "../classes"

import { getPreviewStyles, setHiddenFields, hideStudyDetailsKeys } from "./utils/utils"

const hideFields = {
  root: [
    "type_of_research",
    "ctis_form",
    "mscs",
    "sponsors",
    "protocol_information",
    // "eudract_number",
    "overview_of_pharmaceuticals",
    "investigator_information",
    "non_investigator_information",
    "information_on_multicenter_clinical_trials",
    "monetary_sponsor",
    "irb_information",
    "secondary_attributes",
    "ema_hma_additional_information",
  ],
  study_identification: {
    root: [
      "eupas_registry_number",
      "darwin_eu_study",
      "study_topic",
      "registered_in_jrct_registry",
      "summary_of_changes",
      "country_regulator_not_listed",
      "emarwd_official_title_and_acronym",
      "other_study_ids",
      "regulatory_procedure_number",
      "study_topic_other",
    ],
  },
  contact_information: {
    root: [
      "investigator_contact_details",
      "paediatric_investigation_plan",
      "investigator_center",
      "department_research_group",
      "encepp_center_organization_affiliation",
      "encepp_center_website",
      "networks_registered_encepp",
      "other_networks",
      "centers_registered_encepp",
      "other_centers",
      "institution_conducting_study",
      "institution_conducting_study_if_not_in_list",
      "additional_institutions",
      "additional_institutions_if_not_in_list",
      "country_study_is_conducted",
    ],
  },
  study_ipd: ["contents_of_informed_consent_for_study", "other_registries", "notes", "supplementary_information"],
  study_timeline: {
    root: [
      "plan_sponsor_audit",
      "compensation_and_medical_care_in_the_event_of_health_damage",
      "milestone_dates",
      "signed_funding_contract_date",
      // "data_collection_start_date",
      "data_analysis_start_date",
      "interim_report_date",
      "final_study_report_date",
      "more_details_sources_of_funding",
      "other_funding_sources",
      "more_details_sources_of_funding",
    ],
    recruitment: ["research_results", "summary_results"],
  },
  study_description: {
    root: [
      "start_date_of_enrollment",
      "study_start_date_in_japan",
      "inclusion_criteria",
      "exclusion_criteria",
      "discontinuation_criteria",
      "medical_conditions",
      "additional_medical_conditions",
      "target_disease_code",
      "presence_of_interventions",
      "interventions",
      "intervention_code",
      "intervention_keywords",
      "primary_outcome_measures",
      "secondary_outcome_measures",
      "additional_medical_conditions",
      "trial_scope",
      "ctis_trial_phase",
      "brief_summary_translations",
      "secondary_objective",
      "secondary_objective_translations",
      "low_intervention_trial",
      "therapeutic_area",
      "meddra",
      "principle_inclusion_criteria",
      "principle_exlusion_criteria",
      "protocol_information",
      "scientific_advice_and_pip",
      "associated_clinical_trials",
      "sponsors",
      "products",
    ],
  },
  study_scope: {
    root: [
      "scope_of_study",
      "other_scope_description",
      "design_of_study",
      "trial_scope",
      "ctis_trial_phase",
      "periods",
      "countries_outside_eea",
      "control",
      "placebo",
      "blinding",
      "randomization",
      "non_insurance_combined_medical_expenses",
      "countries_of_recruitment_except_japan",
      "jrct_masking",
      "clinical_trial_regulatory_scope",
      "clinical_trial_type",
      "data_collection_method",
      "other_non_interventional_study_design",
      "summary_results",
    ],
  },
  study_eligibility: {
    root: [
      "population_under_study",
      "sources_of_data",
      "registered_data_sources",
      "other_registered_data_sources",
      "other_data_source",
      "principle_inclusion_criteria",
      "principle_exlusion_criteria",
      "is_clinical_study",
      "short_description_of_study_population",
      "age_groups",
      "special_interest_of_population",
      "other_special_interest_of_population",
    ],
  },
  study_arms: {
    root: [
      "drug_type",
      "atc_code",
      "substance_inns",
      "atc_code",
      "product_name",
      "country",
      "products",
      "setting",
      "eupas_interventions",
      "comparators",
      "medicine_name",
      "other_medicine_name",
    ],
  },
  study_reference: {
    root: [
      "associated_clinical_trials",
      "is_requesting_encepp_seal",
      // "make_full_protocol_public_when_study_ends",
      "study_publications",
      "encepp_code_of_conduct",
    ],
  },
  study_outcome_measures: {
    root: ["outcome_measures_ctis", "eupas_outcomes", "statistical_analysis_plan"],
  },
}

const showFields = {
  study_identification: ["brief_title", "acronym", "official_title", "secondary_ids"],
}

export const ctgProtocolUISchema = (configs) => {
  let preview = configs?.preview
  let formData = configs?.formData
  const disableValidation = configs?.disableValidation || false

  let returnObj = {
    "ui:options": {
      classNames: {
        objectContainer: "rjs-full",
        field: "rjs-full",
      },
    },
    protocol: {
      "ui:order": [
        "protocol_id",
        "study_type",
        // "eudract_number",
        "study_identification",
        "contact_information",
        "study_oversight",
        "secondary_attributes",
        "study_timeline",
        "milestone_dates",
        "*",
      ],
      "ui:options": {
        title: "Study Identification and Information",
        classNames: protocolClassNames,
      },
      protocol_id: {
        "ui:options": {
          label: false,
          required: true,
          title: "Unique Protocol ID",
          charCountEnabled: true,
          maxChars: 30,
          disabled: true,
          hideFork: true,
          disableValidation,
          classNames: {
            field: "rjs-col-lg",
          },
        },
      },
      study_type: {
        "ui:options": {
          title: "Study Type",
          placeholder: "Select Study Type",
          label: false,
          required: true,
          disableValidation,
          hideFork: true,
          translations: {
            jp: {
              title: "試験等の種類",
            },
          },
          classNames: {
            field: "rjs-col-lg",
          },
        },
      },
      study_identification: {
        "ui:order": ["protocol_id", "acronym", "brief_title", "official_title", "*"],
        "ui:options": {
          classNames: firstProtocolSectionClassNames,
        },
        brief_title: {
          "ui:options": {
            label: false,
            required: true,
            disableValidation,
            title: "Brief Title",
            placeholder: "Enter Brief Title",
            charCountEnabled: true,
            maxChars: 300,
            validations: [
              {
                regex: /^.{18,}$/,
                message: "A title this short cannot be sufficiently descriptive.",
              },
              {
                regex: /^.{24,}$/,
                message: "A title, this short is probably not sufficiently descriptive.",
              },
              {
                regex: /^.{36,}$/,
                message: "A title, this short may not be sufficiently descriptive.",
              },
              {
                regex: /^.{0,120}$|.{301,}$/,
                message: "Brief Title should have no more than 120 characters.",
              },
            ],
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        acronym: {
          "ui:options": {
            title: "Acronym (if any)",
            label: false,
            charCountEnabled: true,
            maxChars: 14,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        official_title: {
          "ui:options": {
            label: false,
            required: true,
            disableValidation,
            title: "Official Title",
            placeholder: "Enter Official Title",
            charCountEnabled: true,
            maxChars: 600,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        secondary_ids: {
          "ui:options": {
            title: "Secondary ID's",
            itemTitle: "Identifier",
            addButtonTitle: "Add Secondary ID",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            "ui:order": ["secondary_id_type", "id", "domain", "*"],
            secondary_id_type: {
              "ui:options": {
                label: false,
                title: "Type",
                hideFork: true,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
                validations: [
                  {
                    regex:
                      /^(?!WHO Universal Trial Number \(UTN\)$|ClinicalTrials\.gov identifier \(NCT number\)$|ISRCTN number$).*/,
                    message: "This is a CTIS specific value and will be submitted as other registry in PRS",
                    type: "NOTE",
                  },
                ],
              },
            },
            id: {
              "ui:options": {
                label: false,
                title: "data-schema", // data-schema title will pull title from data schema
                charCountEnabled: true,
                hideFork: true,
                maxChars: 30,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            domain: {
              "ui:options": {
                label: false,
                title: "data-schema", // data-schema title will pull title from data schema
                charCountEnabled: true,
                hideFork: true,
                maxChars: 119,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
          },
        },
        ...setHiddenFields(
          Object.keys({
            ...study_identification.properties,
            ...jrctDataSchema.study_identification.clinical_trial_post_marketing_surveillance,
            ...jrctDataSchema.study_identification.shared,
          }),
          showFields.study_identification
        ),
        ...setHiddenFields(hideFields.study_identification.root),
      },
      contact_information: {
        "ui:order": [
          "sponsor",
          "collaborators",
          "responsible_party",
          "investigator_name",
          "investigator_official_title",
          "investigator_affiliation",
          "*",
        ],
        "ui:options": {
          title: "Study Sponsor, Collaborators and Contact Information",
          classNames: objectClassNames.primary,
        },
        responsible_party: {
          "ui:options": {
            title: "Responsible Party",
            placeholder: "Select Responsible Party",
            label: false,
            required: true,
            disableValidation,
            tooltip:
              "Select Sponsor unless the Principal Investigator has been designated as Responsible Party or the Principal Investigator is the Sponsor.",
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        sponsor: {
          "ui:options": {
            title: "Sponsor",
            placeholder: "Select Sponsor",
            tooltip:
              "Primary organization conducting study and associated data analysis (not necessarily a funding source).",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "Lookahead", preview }),
        },
        investigator_name: {
          "ui:options": {
            title: "Investigator Name",
            placeholder: "Enter Investigator Name",
            label: false,
            // required: true,
            disableValidation,
            tooltip:
              "Select Sponsor unless the Principal Investigator has been designated as Responsible Party or the Principal Investigator is the Sponsor.",
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        investigator_official_title: {
          "ui:options": {
            title: "Investigator Official Title",
            placeholder: "Enter Official Title",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 254,
            label: false,
            tooltip:
              "Select Sponsor unless the Principal Investigator has been designated as Responsible Party or the Principal Investigator is the Sponsor.",
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        investigator_affiliation: {
          "ui:options": {
            title: "Investigator Affiliation",
            placeholder: "Enter Investigator Affiliation",
            label: false,
            // required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 160,
            tooltip:
              "Select Sponsor unless the Principal Investigator has been designated as Responsible Party or the Principal Investigator is the Sponsor.",
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        collaborators: {
          "ui:options": {
            title: "Collaborators",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "FreeMultiSelect", preview }),
        },
        central_contacts: {
          "ui:options": {
            title: "Central Contact",
            classNames: objectClassNames.contact_root_container,
          },
          central_contacts_first_name: {
            "ui:options": {
              label: false,
              title: "First Name",
              placeholder: "First Name",
              charCountEnabled: true,
              maxChars: 62,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          central_contacts_middle_name: {
            "ui:options": {
              label: false,
              title: "M.I",
              placeholder: "Enter MI",
              charCountEnabled: true,
              maxChars: 10,
              classNames: {
                fieldContainer: "rjs-col-xs",
                field: "rjs-col-xs",
              },
            },
          },
          central_contacts_last_name: {
            "ui:options": {
              label: false,
              title: "Last Name",
              charCountEnabled: true,
              maxChars: 62,
              classNames: {
                fieldContainer: "fill-width-50",
                field: "rjs-col-lg",
              },
            },
          },
          central_contacts_degree: {
            "ui:options": {
              label: false,
              title: "Degree",
              placeholder: "Enter Degree",
              charCountEnabled: true,
              maxChars: 30,
              classNames: {
                fieldContainer: "rjs-col-sm",
              },
            },
          },
          central_contacts_phone_number: {
            "ui:options": {
              label: false,
              title: "Phone",
              charCountEnabled: true,
              maxChars: 30,
              classNames: {
                fieldContainer: "rjs-col-md",
                field: "rjs-col-md",
              },
            },
          },
          central_contacts_ext: {
            "ui:options": {
              label: false,
              title: "Ext.",
              placeholder: "Enter Extension",
              charCountEnabled: true,
              maxChars: 14,
              classNames: {
                fieldContainer: "rjs-col-sm",
                field: "rjs-col-sm",
              },
            },
          },
          central_contacts_email: {
            "ui:options": {
              label: false,
              title: "Email",
              placeholder: "Enter Email",
              charCountEnabled: true,
              maxChars: 254,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
        },
        central_contacts_backup: {
          "ui:options": {
            title: "Central Contact Backup",
            classNames: objectClassNames.contact_root_container,
          },
          central_contacts_backup_first_name: {
            "ui:options": {
              label: false,
              title: "First Name",
              placeholder: "First Name",
              charCountEnabled: true,
              maxChars: 62,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          central_contacts_backup_middle_name: {
            "ui:options": {
              label: false,
              title: "M.I",
              placeholder: "Enter MI",
              charCountEnabled: true,
              maxChars: 10,
              classNames: {
                fieldContainer: "rjs-col-xs",
                field: "rjs-col-xs",
              },
            },
          },
          central_contacts_backup_last_name: {
            "ui:options": {
              label: false,
              title: "Last Name",
              placeholder: "Enter Last Name",
              charCountEnabled: true,
              maxChars: 62,
              classNames: {
                fieldContainer: "fill-width-50",
                field: "rjs-col-lg",
              },
            },
          },
          central_contacts_backup_degree: {
            "ui:options": {
              label: false,
              title: "Degree",
              placeholder: "Enter Degree",
              charCountEnabled: true,
              maxChars: 30,
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          central_contacts_backup_phone_number: {
            "ui:options": {
              label: false,
              title: "Phone",
              charCountEnabled: true,
              maxChars: 30,
              classNames: {
                fieldContainer: "rjs-col-md",
                field: "rjs-col-md",
              },
            },
          },
          central_contacts_backup_ext: {
            "ui:options": {
              label: false,
              title: "Ext.",
              placeholder: "Enter Extension",
              charCountEnabled: true,
              maxChars: 14,
              classNames: {
                fieldContainer: "rjs-col-sm",
                field: "rjs-col-sm",
              },
            },
          },
          central_contacts_backup_email: {
            "ui:options": {
              label: false,
              title: "Email",
              placeholder: "Enter Email",
              charCountEnabled: true,
              maxChars: 254,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
        },
        overall_study_officials: {
          "ui:options": {
            title: "Overall Study Officials",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            addButtonTitle: "Add Study Official",
            classNames: arrayClassNames.primary,
          },
          items: {
            overall_study_officials_first_name: {
              "ui:options": {
                title: "First Name",
                placeholder: "Enter First Name",
                label: false,
                charCountEnabled: true,
                maxChars: 62,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            overall_study_officials_middle_name: {
              "ui:options": {
                title: "M.I",
                placeholder: "Enter MI",
                charCountEnabled: true,
                maxChars: 10,
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xs",
                  field: "rjs-col-xs",
                },
              },
            },
            overall_study_officials_last_name: {
              "ui:options": {
                title: "Last Name",
                placeholder: "Enter Last Name",
                label: false,
                charCountEnabled: true,
                maxChars: 62,
                classNames: {
                  fieldContainer: "fill-width-50",
                  field: "rjs-col-lg",
                },
              },
            },
            overall_study_officials_degree: {
              "ui:options": {
                title: "Degree",
                placeholder: "Enter Degree",
                label: false,
                charCountEnabled: true,
                maxChars: 30,
                classNames: {
                  fieldContainer: "rjs-col-sm",
                },
              },
            },
            overall_study_officials_organization_affiliation: {
              "ui:options": {
                title: "Organization Affiliation",
                placeholder: "Enter Organization Affiliation",
                label: false,
                charCountEnabled: true,
                maxChars: 254,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            official_role: {
              "ui:options": {
                title: "Official's Role",
                placeholder: "Select Role",
                label: false,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": "radio",
            },
          },
        },
        ...setHiddenFields(hideFields.contact_information.root),
      },
      study_oversight: {
        "ui:options": {
          title: "Oversight",
          classNames: objectClassNames.primary,
        },
        us_drug: {
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          us_fda_drug: {
            "ui:options": {
              title: "U.S. FDA-regulated Drug",
              placeholder: "--Select--",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          productexported_from_us: {
            "ui:options": {
              title: "Product Exported from U.S.",
              placeholder: "--Select--",
              label: false,
              tooltip: `"Product Exported from U.S." is required only if below conditions are met:\n
              1. Either "U.S. FDA-regulated Drug" or "U.S. FDA-regulated Device" is set to "Yes"\n
              2. "U.S. FDA IND/IDE" value is set to "No"`,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
        },
        us_device: {
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
          us_fda_device: {
            "ui:options": {
              title: "U.S. FDA-regulated Device",
              placeholder: "--Select--",
              errorMessage: "FDA Device has not been selected.",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          unapproved_uncleared_device: {
            "ui:options": {
              title: "Unapproved/Uncleared Device",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-full",
                objectTitle: "rjs-h2",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
            device: {
              "ui:options": {
                title: "Unapproved/Uncleared Device",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            post_prior_to_approval_clearance: {
              "ui:options": {
                title: "Post Prior to Approval/Clearance",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
          post_prior_to_approval_clearance: {
            "ui:options": {
              title: "Post Prior to Approval/Clearance",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          pediatric_postmarket_surveillance: {
            "ui:options": {
              title: "Pediatric Postmarket Surveillance",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
        },
        us_ind: {
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          us_fda_ind_ide: {
            "ui:options": {
              title: "U.S. FDA IND/IDE",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          fda_center: {
            "ui:options": {
              title: "FDA Center",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-2xl",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          ind_ide_number: {
            "ui:options": {
              title: "IND/IDE Number",
              placeholder: "Enter Value",
              label: false,
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 30,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
          ind_serial_number: {
            "ui:options": {
              title: "IND Serial Number",
              placeholder: "Enter Value",
              label: false,
              charCountEnabled: true,
              maxChars: 30,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
          availability_of_expanded_access: {
            "ui:options": {
              title: "Availability of Expanded Access",
              placeholder: "--Select--",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-full",
                field: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          expanded_access_record: {
            "ui:options": {
              title: "Expanded Access Record",
              placeholder: "Enter value",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
        },
        human_subjects_protection_review: {
          "ui:options": {
            label: true,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          board_status: {
            "ui:options": {
              title: "Human Subjects Protection Review Board Status",
              placeholder: "--Select--",
              errorMessage: "Review board status is a required field",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-full",
                field: "rjs-col-lg",
              },
            },
          },
          approvalnumber: {
            "ui:options": {
              title: "Approval Number",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          board_name: {
            "ui:options": {
              title: "Board Name",
              placeholder: "Enter name",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          board_affiliation: {
            "ui:options": {
              title: "Board Affiliation",
              placeholder: "Enter affiliation",
              label: false,
              maxChars: 255,
              charCountEnabled: true,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          board_contact: {
            "ui:options": {
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            phone: {
              "ui:options": {
                title: "Phone",
                placeholder: "Enter phone number",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-md",
                  field: "rjs-col-md",
                },
              },
            },
            extension: {
              "ui:options": {
                title: "Ext.",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            email: {
              "ui:options": {
                title: "Email",
                placeholder: "Enter Email Id",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            address: {
              "ui:options": {
                title: "Address",
                placeholder: "Enter address",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        data: {
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          data_monitoring_committee: {
            "ui:options": {
              title: "Data Monitoring Committee",
              placeholder: "--Select--",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
        },
        fda: {
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          fda_regulated_intervention: {
            "ui:options": {
              title: "FDA Regulated Intervention",
              placeholder: "--Select--",
              required: true,
              disableValidation,
              label: false,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          section_801_clinical_trial: {
            "ui:options": {
              title: "Section 801 Clinical Trial",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
        },
      },
      study_timeline: {
        "ui:options": {
          title: "Study Timelines and Status",
          classNames: objectClassNames.primary,
        },
        record_verification_date: {
          "ui:options": {
            title: "Record Verification Date",
            label: false,
            required: true,
            disableValidation,
            classNames: objectClassNames.tertiary,
          },
          select_month: {
            "ui:options": {
              title: "Select Month",
              placeholder: "Select Month",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-sm",
              },
            },
          },
          year: {
            "ui:options": {
              title: "Year",
              placeholder: "Enter Year",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-sm",
              },
            },
          },
        },
        recruitment: {
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          ...setHiddenFields(hideFields.study_timeline.recruitment),
          recruitment_status: {
            "ui:options": {
              title: "Overall Recruitment Status",
              placeholder: "Select Status",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
          study_stopped: {
            "ui:options": {
              title: "Why Study Stopped?",
              placeholder: "Enter Reason",
              label: false,
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 250,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
          },
        },
        expanded: {
          "ui:options": {
            classNames: {
              arrayTitle: "rjs-h3",
              fieldContainer: "rjs-full",
              arrayContainer: "p-24 rjs-border-radius",
              arrayItemsContainer: "flex flex-col gap-24",
              actionContainer: "flex justify-end",
            },
          },
          expanded_status: {
            "ui:options": {
              title: "Expanded Access Status",
              placeholder: "Select Status",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
        },
        ...setHiddenFields(hideFields.study_timeline.root),
      },
      study_description: {
        "ui:order": ["brief_summary", "detailed_description", "*"],
        "ui:options": {
          title: "Study Description and Conditions",
          classNames: objectClassNames.primary,
        },
        brief_summary: {
          "ui:options": {
            title: "Brief Summary",
            placeholder: "Enter Brief Summary",
            label: false,
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 5000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        detailed_description: {
          "ui:options": {
            title: "Detailed Description",
            placeholder: "Enter Detailed Description",
            label: false,
            charCountEnabled: true,
            maxChars: 32000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        study_conditions: {
          "ui:options": {
            title: "Conditions",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "FreeMultiSelect", preview }),
        },
        study_keywords: {
          "ui:options": {
            title: "Keywords",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "SelectTag", preview }),
        },
        ...setHiddenFields(hideFields.study_description.root),
      },
      study_scope: {
        "ui:order": [
          "patient_registry",
          "primary_purpose",
          "study_phase",
          "patient",
          "follow_up_duration",
          "duration_type",
          "additional_patient_registry_data",
          "*",
        ],
        "ui:options": {
          title: "Study Scope and Design",
          classNames: objectClassNames.primary,
        },
        patient_registry: {
          "ui:options": {
            title: "Patient Registry",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-md",
            },
          },
          "ui:widget": getWidget({ type: "CustomCheckboxGroup", preview }),
        },
        primary_purpose: {
          "ui:options": {
            title: "Primary Purpose",
            placeholder: "Select Purpose",
            errorMessage: "Primary Purpose has not been entered",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-1-3",
            },
          },
        },
        study_phase: {
          "ui:options": {
            title: "Study Phase",
            placeholder: "Select Study Phase",
            errorMessage: "Phase cannot be N/A for a study of a U.S. FDA-regulated drug",
            tooltip: "Use N/A for trials that do not involve drug or biologic products",
            label: false,
            required: true,
            disableValidation,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        interventional_study_model: {
          "ui:options": {
            title: "Interventional Study Model",
            placeholder: "Select Interventional Study Model",
            errorMessage: "Interventional Study Model has not been entered",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        model_description: {
          "ui:options": {
            title: "Model Description",
            placeholder: "Enter Study Model description",
            label: false,
            charCountEnabled: true,
            maxChars: 1000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        number_of_arms: {
          "ui:options": {
            title: "Number of Arms/Groups/Cohorts in the study",
            placeholder: "Enter Number of Arms",
            errorMessage: "Number of Arms has not been entered",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-xl",
              field: "rjs-col-md",
            },
          },
        },
        masking: {
          "ui:options": {
            title: "Masking",
            tooltip: "Check all roles that are masked or check None",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "CustomCheckboxGroup", preview }),
        },
        masking_description: {
          "ui:options": {
            title: "Masking Description",
            placeholder: "Enter Masking description",
            label: false,
            charCountEnabled: true,
            maxChars: 1000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        allocation: {
          "ui:options": {
            title: "Allocation",
            tooltip: "Select N/A for single-arm studies",
            label: false,
            required: true,
            orientation: "vertical",
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        enrollment_type: {
          "ui:options": {
            title: "Enrollment Type",
            placeholder: "Select Participant Type",
            errorMessage: "Enrollment Type has not been entered",
            label: false,
            required: true,
            disableValidation,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        enrollment_type_obs: {
          "ui:options": {
            title: "Enrollment Type",
            placeholder: "Select Participant Type",
            errorMessage: "Enrollment Type has not been entered",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        follow_up_duration: {
          "ui:options": {
            title: "Target Follow Up Duration",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-1-3",
            },
          },
        },
        duration_type: {
          "ui:options": {
            title: "Duration",
            placeholder: "--Select--",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        additional_patient_registry_data: {
          "ui:options": {
            title: "Additional Patient Registry Data",
            placeholder: "[The AHRQ Registry of Patient Registries (RoPR) has been discontinued.]",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        observational_study_model: {
          "ui:options": {
            title: "Observational Study Model",
            placeholder: "Select Observational Study Model",
            required: true,
            disableValidation,
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        time_perspective: {
          "ui:options": {
            title: "Time Perspective",
            placeholder: "Select Time Perspective",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-md",
            },
          },
        },
        biospecimen: {
          "ui:options": {
            classNames: {
              arrayTitle: "rjs-h3",
              fieldContainer: "rjs-full",
              arrayContainer: "rjs-full p-24 rjs-border-radius",
              arrayItemsContainer: "flex flex-col gap-24",
              actionContainer: "flex justify-end",
            },
          },
          biospeciment_retention: {
            "ui:options": {
              title: "Biospecimen Retention",
              placeholder: "Select Biospecimen Retention",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-1-3",
              },
            },
          },
          biospecimen_description: {
            "ui:options": {
              title: "Biospecimen Description",
              placeholder: "Enter Description",
              label: false,
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 1000,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
        },
        enrollment_number: {
          "ui:options": {
            title: "Enrollment Number/Number of Subjects",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-xl",
              field: "rjs-col-sm",
            },
          },
        },
        number_of_cohorts: {
          "ui:options": {
            title: "Number of Cohorts in the Study",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-xl",
              field: "rjs-col-md",
            },
          },
        },
        selection: {
          "ui:options": {
            tooltip: "Check all that apply",
            title: "Expanded Access Type",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "CustomCheckboxGroup", preview }),
        },
        ...setHiddenFields(hideFields.study_scope.root),
      },
      study_arms: {
        "ui:options": {
          title: "Arms and Interventions",
          classNames: objectClassNames.primary,
        },
        arms: {
          "ui:options": {
            label: false,
            classNames: objectClassNames.tertiary,
          },
          "ui:widget": getWidget({ type: "SimpleTable", preview }),
        },
        interventions: {
          "ui:options": {
            label: false,
            classNames: objectClassNames.tertiary,
          },
          "ui:widget": getWidget({ type: "SimpleTable", preview }),
        },
        crossref: {
          "ui:options": {
            label: false,
            classNames: objectClassNames.tertiary,
          },
          "ui:widget": getWidget({ type: "CrossReferenceTable", preview }),
        },
        ...setHiddenFields(hideFields.study_arms.root),
      },
      study_eligibilty: {
        "ui:options": {
          title: "Eligibility",
          classNames: objectClassNames.primary,
        },
        sex: {
          "ui:options": {
            title: "Sex",
            placeholder: "--Select--",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        gender_based: {
          "ui:options": {
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          gender_based_1: {
            "ui:options": {
              title: "Gender Based",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-1-3",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          gender_eligibility_description: {
            "ui:options": {
              title: "Gender Eligibility Description",
              placeholder: "Enter Gender Eligibility Description",
              label: false,
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 1000,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
        },
        age_limits: {
          "ui:options": {
            title: "Age Limits",
            label: false,
            classNames: objectClassNames.tertiary,
          },
          minimum: {
            "ui:options": {
              title: "Minimum age",
              translations: {
                jp: {
                  title: "最低年齢",
                },
              },
              classNames: objectClassNames.tertiary,
            },
            minimum_age_type: {
              "ui:options": {
                title: "Type",
                placeholder: "--Select--",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-sm",
                },
                translations: {
                  jp: {
                    title: "単位",
                  },
                },
              },
            },
            minimum_age_value: {
              "ui:options": {
                title: "Age",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xs",
                },
                translations: {
                  jp: {
                    title: "年齢",
                  },
                },
                validations: [
                  {
                    regex: /^(?!0{2,}$)\d{0,3}$/,
                    message: "Acceptable value is from 0 to 999",
                  },
                ],
              },
            },
          },
          maximum: {
            "ui:options": {
              title: "Maximum age",
              translations: {
                jp: {
                  title: "最大年齢",
                },
              },
              classNames: objectClassNames.tertiary,
            },
            maximum_age_type: {
              "ui:options": {
                title: "Type",
                placeholder: "--Select--",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-sm",
                },
                translations: {
                  jp: {
                    title: "単位",
                  },
                },
              },
            },
            maximum_age_value: {
              "ui:options": {
                title: "Age",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xs",
                },
                translations: {
                  jp: {
                    title: "年齢",
                  },
                },
                validations: [
                  {
                    regex: /^(?!0{2,}$)\d{0,3}$/,
                    message: "Acceptable value is from 0 to 999",
                  },
                ],
              },
            },
          },
        },
        accept_healthy_volunteers: {
          "ui:options": {
            title: "Accept Healthy Volunteers",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-2xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        eligibility_criteria: {
          "ui:options": {
            title: "Eligibility Criteria",
            label: false,
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 20000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        study_population_description: {
          "ui:options": {
            title: "Study Population Description",
            placeholder: "Enter Study Population Description",
            label: false,
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 1000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        sampling_method: {
          "ui:options": {
            title: "Sampling Method",
            placeholder: "--Select--",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        ...setHiddenFields(hideFields.study_eligibility.root),
      },
      study_ipd: {
        "ui:options": {
          title: "IPD Sharing Statements",
          classNames: objectClassNames.primary,
        },
        plan_to_share_IPD: {
          "ui:options": {
            title: "Plan to Share IPD?",
            placeholder: "--Select--",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        plan_description: {
          "ui:options": {
            title: "Plan Description",
            errorMessage: "IPD Description must have no more than 1000 characters",
            label: false,
            charCountEnabled: true,
            maxChars: 1000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        ipd_sharing_info: {
          "ui:options": {
            title: "Supporting Information Type",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "CustomCheckboxGroup", preview }),
        },
        time_frame: {
          "ui:options": {
            title: "Time Frame",
            errorMessage: "IPD Sharing Time Frame must have no more than 1000 characters.",
            label: false,
            charCountEnabled: true,
            maxChars: 1000,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        access_criteria: {
          "ui:options": {
            title: "Access Criteria",
            label: false,
            charCountEnabled: true,
            maxChars: 1000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        url_text: {
          "ui:options": {
            title: "URL",
            errorMessage: "IPD Sharing URL must have no more than 3999 characters.",
            label: false,
            charCountEnabled: true,
            maxChars: 3999,
            classNames: {
              fieldContainer: "rjs-col-2xl",
            },
          },
        },
        ...setHiddenFields(hideFields.study_ipd),
      },
      study_outcome_measures: {
        "ui:options": {
          title: "Study Endpoints / Outcome Measures",
          classNames: objectClassNames.primary,
        },
        outcome_measures: {
          "ui:options": {
            title: "Outcome Measures",
            addButtonTitle: "Add Outcome Measures",
            arrayItemTitle: "Outcome Measure",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            canCopy: true,
            canCopyOutcomeMeasure: true,
            showIndex: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            title: {
              "ui:options": {
                title: "Title",
                placeholder: "Enter Title",
                label: false,
                required: true,
                disableValidation,
                hideFork: true,
                rows: 1,
                charCountEnabled: true,
                maxChars: 254,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            time_frame: {
              "ui:options": {
                title: "Time Frame",
                placeholder: "Enter Time Frame",
                label: false,
                required: true,
                disableValidation,
                hideFork: true,
                charCountEnabled: true,
                maxChars: 254,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
            description: {
              "ui:options": {
                title: "Description",
                placeholder: "Enter Description",
                label: false,
                hideFork: true,
                charCountEnabled: true,
                maxChars: 999,
                hideFork: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            type: {
              "ui:options": {
                title: "Type",
                label: false,
                required: true,
                disableValidation,
                hideFork: true,
                orientation: "horizontal",
                hideFork: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
        },
        ...setHiddenFields(hideFields.study_outcome_measures.root),
      },
      study_reference: {
        "ui:options": {
          title: "Citations, References and URLs",
          classNames: objectClassNames.primary,
        },
        citations: {
          "ui:options": {
            title: "Citations",
            addButtonTitle: "Add Citation",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            pub_med_id: {
              "ui:options": {
                title: "PubMed ID",
                label: false,
                charCountEnabled: true,
                maxChars: 14,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            result_reference: {
              "ui:options": {
                title: "Results Reference",
                placeholder: "--Select--",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            citation_text: {
              "ui:options": {
                title: "Enter Citation Text",
                errorMessage: "The citation description should be upto 2000 characters and cannot be editable",
                label: false,
                charCountEnabled: true,
                maxChars: 5000,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        links: {
          "ui:options": {
            title: "Links",
            addButtonTitle: "Add Link",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            url_text: {
              "ui:options": {
                title: "URL",
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 3999,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
            description_text: {
              "ui:options": {
                title: "Enter Description",
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 254,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        available: {
          "ui:options": {
            title: "Available IPD/Information",
            addButtonTitle: "Add Data/Information",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            "ui:order": ["data_info", "url_text", "identifier", "description", "comments"],
            data_info: {
              "ui:options": {
                title: "Data/Information Type",
                placeholder: "--Select--",
                label: false,
                required: true,
                disableValidation,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            url_text: {
              "ui:options": {
                title: "URL",
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 3999,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
            identifier: {
              "ui:options": {
                title: "Identifier",
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 30,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            comments: {
              "ui:options": {
                title: "Comments",
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 1000,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            description: {
              "ui:options": {
                title: "Reason",
                placeholder: "Please specify the reason",
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 254,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
          },
        },
        ...setHiddenFields(hideFields.study_reference.root),
      },
      milestone_dates: {
        "ui:options": {
          title: "Milestone Dates",
          classNames: objectClassNames.tertiary,
        },
        fpi: {
          "ui:options": {
            title: "Study Start Date",
            classNames: {
              ...objectClassNames.tertiary,
            },
          },
          baseline: {
            "ui:options": {
              title: "Baseline",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          forecast: {
            "ui:options": {
              title: "Forecast",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
        },
        lpo: {
          "ui:options": {
            title: "LPO/LPLV/SCD/Global end of trial date",
            classNames: {
              ...objectClassNames.tertiary,
              // fieldContainer: "rjs-col-1-2",
            },
          },
          baseline: {
            "ui:options": {
              title: "Baseline",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          forecast: {
            "ui:options": {
              title: "Forecast",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
        },
        pcd: {
          "ui:options": {
            title: "PCD",
            classNames: {
              ...objectClassNames.tertiary,
            },
          },
          baseline: {
            "ui:options": {
              title: "Baseline",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          forecast: {
            "ui:options": {
              title: "Forecast",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
        },
        ...setHiddenFields(hideStudyDetailsKeys.milestone_dates.root),
      },
      ...setHiddenFields(hideStudyDetailsKeys.root),
      ...setHiddenFields(hideFields.root),
    },
  }

  if (preview === true) {
    getPreviewStyles(returnObj, formData)
  }

  return returnObj
}
